import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const generalFaqs = [
  {
    question: "What is AI-First Marketing?",
    answer: "AI-First Marketing is an approach where artificial intelligence is embedded into every aspect of your marketing strategy — from content creation and SEO to paid ads and lead generation. Instead of using AI as an afterthought, we build systems where AI handles repetitive tasks, analyzes data, and optimizes campaigns in real-time, allowing your team to focus on strategy and relationships.",
  },
  {
    question: "How is TheCloudSEO different from traditional marketing agencies?",
    answer: "Traditional agencies rely on large teams executing manual tasks. We're built differently — we use AI-powered systems and automation to deliver better results with less overhead. This means faster turnaround, more consistent output, and marketing that compounds over time instead of resetting every month.",
  },
  {
    question: "Do you work with startups or only established companies?",
    answer: "We work with both. Our systems are designed to scale — whether you're a funded startup looking to establish market presence or an established B2B company wanting to modernize your marketing infrastructure. The key requirement is a commitment to building systems, not just running campaigns.",
  },
  {
    question: "How long does it take to see results?",
    answer: "Timeline varies by service. AI-powered paid ads can show improvements within 2-4 weeks. SEO and GEO typically take 3-6 months to show significant organic growth. AI Agents and automation systems can be deployed and generating value within 2-4 weeks. We set clear expectations during our initial strategy session.",
  },
  {
    question: "What industries do you specialize in?",
    answer: "We primarily work with B2B companies across technology, SaaS, professional services, and cloud infrastructure sectors. Our expertise lies in complex sales cycles where education-based marketing and thought leadership drive pipeline — not just clicks.",
  },
  {
    question: "Can you integrate with our existing tools and CRM?",
    answer: "Absolutely. We design our systems to work with your existing stack — whether that's HubSpot, Salesforce, Pipedrive, or other platforms. Our automation and AI agents can connect to virtually any tool through APIs, webhooks, or native integrations.",
  },
  {
    question: "What's included in an AI Growth Audit?",
    answer: "Our AI Growth Audit is a comprehensive 30-minute strategy session where we analyze your current marketing infrastructure, identify automation opportunities, evaluate your AI readiness, and outline a roadmap for building scalable marketing systems. It's free, no-obligation, and focused on providing actionable insights.",
  },
  {
    question: "What's your pricing model?",
    answer: "We offer flexible engagement models including project-based work, monthly retainers, and performance-based arrangements depending on the service. Our pricing reflects the value of AI-powered systems that reduce your need for additional headcount while delivering consistent results. Book a call to discuss your specific needs and get a custom proposal.",
  },
];

const geoFaqs = [
  {
    question: "What is Generative Engine Optimization (GEO)?",
    answer: "GEO is the evolution of SEO for the AI era. As more people use AI assistants like ChatGPT, Perplexity, and Google's AI Overviews to find answers, traditional SEO isn't enough. GEO ensures your brand appears in AI-generated responses by optimizing your content structure, authority signals, and data sources that AI models reference.",
  },
  {
    question: "How is GEO different from traditional SEO?",
    answer: "Traditional SEO focuses on ranking in search engine results pages (SERPs). GEO focuses on getting your brand cited and referenced by AI systems. This requires different optimization strategies — structured data, authoritative content, clear entity relationships, and presence across sources that AI models are trained on or reference.",
  },
  {
    question: "Will GEO replace my existing SEO efforts?",
    answer: "No, GEO complements SEO. Strong SEO fundamentals actually help with GEO — authoritative content, technical optimization, and backlinks all contribute to how AI models perceive your brand. We build on your existing SEO foundation while adding the layers needed for AI visibility.",
  },
  {
    question: "How do you measure GEO success?",
    answer: "We track AI mentions and citations across major platforms (ChatGPT, Perplexity, Google AI Overviews), monitor your brand's presence in AI-generated responses, and measure the referral traffic from AI assistants. We also track traditional SEO metrics as they remain foundational to GEO success.",
  },
];

const aiAdsFaqs = [
  {
    question: "How does AI improve paid advertising performance?",
    answer: "AI analyzes vast amounts of data in real-time to optimize bidding strategies, audience targeting, ad creative, and budget allocation. It identifies patterns humans would miss, responds instantly to performance changes, and continuously learns to improve results. This typically leads to 20-40% improvement in ROAS within the first few months.",
  },
  {
    question: "Which ad platforms do you manage?",
    answer: "We specialize in Google Ads, LinkedIn Ads, Meta Ads (Facebook/Instagram), and programmatic display. Our AI systems integrate with all major platforms and can optimize cross-channel campaigns to ensure budget flows to the highest-performing channels automatically.",
  },
  {
    question: "How much ad spend do you typically manage?",
    answer: "We work with B2B companies spending anywhere from $10,000 to $500,000+ per month on paid advertising. Our AI systems scale efficiently — the same intelligence that optimizes a $10K campaign works even better with larger budgets and more data.",
  },
  {
    question: "Do you create the ad creative or just manage campaigns?",
    answer: "We do both. Our team creates ad copy, designs visuals, and produces video content — all optimized based on AI insights about what performs best for your audience. We also A/B test creative variations continuously to improve performance over time.",
  },
];

const aiContentFaqs = [
  {
    question: "How do you use AI for content creation?",
    answer: "We use AI to accelerate research, generate first drafts, optimize for SEO, and personalize content at scale. However, every piece of content is reviewed and refined by human strategists to ensure accuracy, brand voice consistency, and genuine value. AI handles the heavy lifting; humans ensure quality.",
  },
  {
    question: "Will AI-generated content hurt my SEO?",
    answer: "Not when done correctly. Google doesn't penalize AI-generated content — they penalize low-quality content regardless of how it's produced. Our approach uses AI as a tool within a human-led process, ensuring every piece meets quality standards and provides genuine value to readers.",
  },
  {
    question: "What types of content do you produce?",
    answer: "We produce blog posts, whitepapers, case studies, email sequences, social media content, landing pages, and thought leadership pieces. All content is strategically aligned with your buyer journey and optimized for both human readers and AI systems.",
  },
  {
    question: "How do you maintain brand voice with AI assistance?",
    answer: "We create detailed brand voice guidelines and train our AI workflows on your existing content. Every piece goes through human review to ensure consistency. Over time, our systems learn your preferences and produce increasingly on-brand first drafts.",
  },
];

export default function FAQs() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-background pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-sm font-medium mb-8">
              FAQs
            </span>
            <h1 className="heading-hero mb-6">
              Frequently Asked
              <br />
              <span className="text-muted-foreground">Questions</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about our AI-powered marketing services
              and how we can help your business grow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* General FAQs Section */}
      <section className="py-20">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h2 className="text-2xl font-bold mb-8">General Questions</h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {generalFaqs.map((faq, index) => (
                  <AccordionItem
                    key={`general-${index}`}
                    value={`general-${index}`}
                    className="bg-background border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* GEO FAQs Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h2 className="text-2xl font-bold mb-8">Generative Engine Optimization (GEO)</h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {geoFaqs.map((faq, index) => (
                  <AccordionItem
                    key={`geo-${index}`}
                    value={`geo-${index}`}
                    className="bg-background border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* AI Ads FAQs Section */}
      <section className="py-20">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h2 className="text-2xl font-bold mb-8">AI-Powered Advertising</h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {aiAdsFaqs.map((faq, index) => (
                  <AccordionItem
                    key={`ads-${index}`}
                    value={`ads-${index}`}
                    className="bg-background border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* AI Content FAQs Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h2 className="text-2xl font-bold mb-8">AI Content & SEO</h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {aiContentFaqs.map((faq, index) => (
                  <AccordionItem
                    key={`content-${index}`}
                    value={`content-${index}`}
                    className="bg-background border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-base leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container-narrow text-center">
          <AnimatedSection>
            <h2 className="heading-section mb-6">Still Have Questions?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Book a free AI Growth Audit and get personalized answers about how
              we can help your business grow.
            </p>
            <button
              className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-10 h-14 text-base font-medium hover:bg-primary/90 transition-colors"
              data-cal-link="thecloudseo/30-min-meeting"
              data-cal-namespace="30-min-meeting"
              data-cal-config='{"layout":"month_view"}'
            >
              Book a Call
            </button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
